import React from "react";
import { useNewsGenerate } from "./NewsGenerateContext";

// Article Preview component that resembles how it would appear on the website
const WebsitePreview: React.FC<{ news: any }> = ({ news }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
      {/* Category Tag */}
      {news.tags && news.tags.length > 0 && (
        <div className="px-4 py-2 bg-blue-600 text-white font-medium uppercase tracking-wider text-sm">
          {news.tags[0]?.title || "News"}
        </div>
      )}

      {/* Hero Image */}
      {news.defaultImage ? (
        <div className="relative w-full h-80">
          <img
            src={news.defaultImage.url}
            alt={news.title}
            className="w-full h-full object-cover"
          />
          {(news.featured || news.trending) && (
            <div className="absolute top-4 right-4 flex space-x-2">
              {news.featured && (
                <span className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded">
                  Featured
                </span>
              )}
              {news.trending && (
                <span className="px-3 py-1 bg-purple-600 text-white text-sm font-bold rounded">
                  Trending
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-80 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-500">No Featured Image</span>
        </div>
      )}

      {/* Article Content */}
      <div className="p-6 md:p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{news.title}</h1>
        <h2 className="text-xl text-gray-600 mb-6">{news.subtitle}</h2>

        {/* Author and Date */}
        <div className="flex items-center mb-8 border-b border-gray-200 pb-4">
          {news?.author?.defaultImage?.url ? (
            <img
              src={news.author.defaultImage.url}
              alt={news.author.name}
              className="w-10 h-10 rounded-full mr-3"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300 mr-3 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {news?.author?.name?.charAt(0) || "A"}
              </span>
            </div>
          )}
          <div>
            <div className="font-medium">{news?.author?.name || "Anonymous"}</div>
            <div className="text-sm text-gray-500">
              {news.createdDate ? new Date(news.createdDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : "Publication Date"}
            </div>
          </div>
          <div className="ml-auto px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
            {news.status || "Draft"}
          </div>
        </div>

        {/* Summary */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500 italic">
          <p className="text-gray-700">{news.summary}</p>
        </div>

        {/* Article Content Preview */}
        <div className="prose max-w-none mb-8">
          <p>{news.article || "Article content will appear here."}</p>
        </div>

        {/* Tags */}
        {news.tags && news.tags.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Tags:</h3>
            <div className="flex flex-wrap gap-2">
              {news.tags.map((tag: any) => (
                <span
                  key={tag.id}
                  className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                >
                  {tag.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metadata Display component
const MetadataDisplay: React.FC<{ news: any }> = ({ news }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Article Metadata</h2>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-500">Title</h3>
          <p className="mt-1">{news.title || "Untitled"}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Subtitle</h3>
          <p className="mt-1">{news.subtitle || "None"}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Status</h3>
          <div className="mt-1 px-2 py-1 inline-block rounded bg-green-100 text-green-800">{news.status || "Draft"}</div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Author</h3>
          <p className="mt-1">{news?.author?.name || "Anonymous"}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Creation Date</h3>
          <p className="mt-1">{news.createdDate ? new Date(news.createdDate).toLocaleString() : "Not set"}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Slug</h3>
          <p className="mt-1">{news.slug || "Not set"}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Flags</h3>
          <div className="mt-1 flex space-x-2">
            {news.featured && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Featured</span>}
            {news.trending && <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">Trending</span>}
            {!news.featured && !news.trending && <span className="text-gray-500">None</span>}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Tags</h3>
          <div className="mt-1 flex flex-wrap gap-1">
            {news.tags && news.tags.length > 0 ? (
              news.tags.map((tag: any) => (
                <span key={tag.id} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                  {tag.title}
                </span>
              ))
            ) : (
              <span className="text-gray-500">None</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FinalizeStep: React.FC = () => {
  const { newsArticle } = useNewsGenerate();

  if (!newsArticle) {
    return (
      <div className="p-4 border rounded-lg shadow-sm bg-white">
        <p className="text-gray-600">No news article has been generated yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="p-4 border rounded-lg shadow-sm bg-white">
        <h2 className="text-xl font-semibold mb-6">Final Review</h2>
        <p className="text-gray-600 mb-6">
          This is how your article will appear on the website. Review all details before publishing.
        </p>

        {/* Website Preview */}
        <div className="border border-gray-200 rounded-lg p-4 overflow-hidden bg-gray-50">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center">
            <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-1"></span>
            <span className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-1"></span>
            <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span>
            <span className="ml-2">Website Preview</span>
          </div>
          <WebsitePreview news={newsArticle} />
        </div>
      </div>

      {/* Article Metadata */}
      <MetadataDisplay news={newsArticle} />
    </div>
  );
};

export default FinalizeStep; 